import { Divider, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BiFilterAlt } from 'react-icons/bi';
import { ClientRequestDTO } from 'wisestep-api';
import {
  formatDateToMonthDayYearString,
  SearchBarNew,
  WsAutoComplete,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
  WsButton,
  WsColors,
  WsColorsNew,
} from 'wisestep-base';

import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { getFilterValue } from '../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { allowedKeys, StyledBox } from '../helper/ClientListingHelper';
import { useIndustries } from '../hooks/useIndustries';

export interface ICreatedOrUpdatedBetween {
  filterByDateType: string;
  fromDate: string;
  toDate: string;
}
export interface IClientFilters {
  requireHeaderBanner: boolean;
  searchQuery: ClientRequestDTO;
  clear: () => void;
  getUpdatedFiltersFromFilters: (data) => void;
  clientStatuses: string[];
  tags: string[];
  defaultFilter: ClientRequestDTO;
  onAdvancedFiltersClick: () => void;
  advFilterApply: ClientRequestDTO;
}

const ClientFilters = ({
  requireHeaderBanner,
  searchQuery,
  clear,
  getUpdatedFiltersFromFilters,
  clientStatuses,
  defaultFilter,
  onAdvancedFiltersClick,
  advFilterApply,
}: IClientFilters) => {
  const getFinalStatus = () =>
    searchQuery.status?.length ? searchQuery.status : defaultFilter.status ?? [];
  const { filterByDateType, fromDate, toDate } = advFilterApply || {};

  const methods = useForm<ClientRequestDTO>({
    defaultValues: {
      ...defaultFilter,
      ...searchQuery,
      status: getFinalStatus(),
    },
  });

  const { handleSubmit, getValues, setValue, reset } = methods;
  const [applyedStatus, setApplyedStatus] = useState<string[]>(
    defaultFilter?.status ?? searchQuery?.status ?? []
  );
  const [applyedOwners, setApplyedOwners] = useState<string[]>(searchQuery?.clientOwners ?? []);
  const [statusValue, setStatusValue] = useState<string[]>(getFinalStatus());
  const { industries } = useIndustries();

  useEffect(() => {
    setApplyedStatus(
      searchQuery?.status?.length ? searchQuery?.status : defaultFilter?.status ?? []
    );
    setApplyedOwners(searchQuery?.clientOwners ?? []);
  }, [searchQuery, defaultFilter]);

  const handleClear = () => {
    setValue('clientOwners', []);
    setValue('status', []);
    reset(defaultFilter);
    setStatusValue([]);
    clear();
    onSearch();
  };

  const onStatusClear = (value: string) => onFilterClearStatus('status', value);

  const onClentClear = (value: string) => onFilterClear('clientOwners', value);

  const onSearch = () => {
    getUpdatedFiltersFromFilters({
      ...searchQuery,
      ...getValues(),
    });
  };

  const onStatusSelection = (statusInput: string[]) => {
    setValue('status', statusInput);
    setStatusValue(statusInput);
  };

  const hasValue = (val: ClientRequestDTO): boolean => {
    if (Array.isArray(val)) return val.length > 0;
    if (val && typeof val === 'object') return Object.values(val).some(hasValue);
    return Boolean(val);
  };

  const hasFilterValue = (): boolean => {
    type AllowedKey = keyof typeof advFilterApply;
    const filtered = allowedKeys.reduce((acc, key) => {
      acc[key] = advFilterApply[key];
      return acc;
    }, {} as Pick<typeof advFilterApply, AllowedKey>);

    const hasContactStatus = (applyedStatus?.length ?? 0) > 0;
    const hasContactOwners = (applyedStatus?.length ?? 0) > 0;
    const advFilterCheck = hasValue(filtered);

    return hasContactStatus || hasContactOwners || advFilterCheck;
  };

  const onCreatedByIdClear = (value: string) => onFilterClear('createdByIds', value);

  const onClearIndustryType = () => setValue('industryType', []);

  const onClearSource = (value: string) => onFilterClear('source', value);

  const onClearTags = (value: string) => onFilterClear('tags', value);

  const onFilterClearStatus = (field: 'status', value: string) => {
    const values = getValues();
    const fieldValues = values[field]?.length ? values[field] : searchQuery.status ?? [];

    const updatedValues: string[] | undefined =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item !== value)
        : undefined;
    setValue(field, updatedValues);
    onSearch();
  };

  const onFilterClear = (
    field:
      | 'createdByIds'
      | 'supervisors'
      | 'tags'
      | 'contactOwners'
      | 'status'
      | 'source'
      | 'clientOwners',
    value: string
  ) => {
    const values = getValues();
    const fieldValues = values[field];
    const updatedValues: string[] | undefined =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item !== value)
        : undefined;
    setValue(field, updatedValues);
    onSearch();
  };

  const onCreatedOrUpdatedBetweenClear = () => {
    setValue('filterByDateType', '');
    setValue('fromDate', '');
    setValue('toDate', '');
    onSearch();
  };

  const onClearClientLocation = () => setValue('location', undefined);

  return (
    <>
      {requireHeaderBanner && (
        <StyledBox sx={{ border: 'none' }}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSearch)}>
              <Grid container bgcolor={WsColors.White} sx={{ ml: -1 }}>
                <Grid item xs={4}>
                  <SearchBarNew
                    size="small"
                    name="query"
                    placeholder="Search Companies"
                    type="submit"
                    onClear={handleClear}
                  />
                </Grid>
                <Grid
                  item
                  container
                  justifyContent="flex-start"
                  alignItems="center"
                  xs={4}
                  spacing={1}
                >
                  <Grid item>
                    <SmartFilter
                      onClear={() => {}}
                      filterName="Company Owners"
                      onClick={() => onSearch()}
                      filterValue={getFilterValue('clientOwners', methods)}
                    >
                      <RecruiterSuggestions
                        name="clientOwners"
                        multiple={true}
                        value={getValues('clientOwners')}
                      />
                    </SmartFilter>
                  </Grid>
                  <Grid item>
                    <SmartFilter
                      onClear={() => {}}
                      filterName="Company status"
                      onClick={onSearch}
                      filterValue={getFilterValue('status', methods)}
                    >
                      <WsAutoComplete
                        key={`status-${statusValue.join(',')}`}
                        onTagsChange={onStatusSelection}
                        placeholder="Eg, Michael"
                        initialValue={statusValue}
                        value={statusValue}
                        optionsInStringArray={clientStatuses}
                        multiple={true}
                        freeSolo={true}
                        name="status"
                        size="small"
                      />
                    </SmartFilter>
                  </Grid>
                </Grid>
                <Grid
                  item
                  container
                  justifyContent="flex-end"
                  xs={4}
                  alignItems="center"
                  spacing={1}
                >
                  <WsButton
                    variant="outlined"
                    size="medium"
                    onClick={onAdvancedFiltersClick}
                    startIcon={<BiFilterAlt />}
                  >
                    Advanced filters
                  </WsButton>
                </Grid>
              </Grid>
              <Grid container pt={2} spacing={1} style={{ display: 'flex', alignItems: 'center' }}>
                {applyedStatus?.map((label, index) => (
                  <Grid item key={index}>
                    <WsBadge
                      label={`Company status: ${label}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onStatusClear(label)}
                      menuList={false}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                ))}
                {applyedOwners?.map((label, index) => (
                  <Grid item key={index}>
                    <WsBadge
                      label={`Company Owners: ${label}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onClentClear(label)}
                      menuList={false}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                ))}
                {advFilterApply?.createdByIds?.map((label, index) => (
                  <Grid item key={index}>
                    <WsBadge
                      label={`Created By: ${label}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onCreatedByIdClear?.(label)}
                      menuList={false}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                ))}
                {(advFilterApply?.industryType?.length ?? 0) > 0 && (
                  <Grid item>
                    <WsBadge
                      label={`Industry Type: ${industries ?? ''}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onClearIndustryType?.()}
                      menuList={false}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                )}
                {advFilterApply?.source?.map((label, index) => (
                  <Grid item key={index}>
                    <WsBadge
                      label={`Source: ${label}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onClearSource?.(label)}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                ))}
                {advFilterApply?.tags?.map((label, index) => (
                  <Grid item key={index}>
                    <WsBadge
                      label={`Tags: ${label}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onClearTags?.(label)}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                ))}
                {advFilterApply?.filterByDateType &&
                  advFilterApply?.fromDate &&
                  advFilterApply?.toDate && (
                    <Grid item>
                      <WsBadge
                      label={`${filterByDateType === 'CREATED_BETWEEN' ? 'Created' : 'Updated'} Between: ${fromDate ? formatDateToMonthDayYearString(new Date(fromDate)) : 'N/A'
                        } - ${toDate ? formatDateToMonthDayYearString(new Date(toDate)) : 'N/A'
                        }`}
                        badgeType={WsBadgetype.INACTIVE}
                        endIcon
                        onClick={onCreatedOrUpdatedBetweenClear}
                        menuList={false}
                        badgeSize={WsBadgeSize.LARGE}
                      />
                    </Grid>
                  )}
                {(!!advFilterApply?.location?.[0]?.city ||
                  !!advFilterApply?.location?.[0]?.country ||
                  !!advFilterApply?.location?.[0]?.state) && (
                  <Grid item>
                    <WsBadge
                      label={`Location: ${[
                        advFilterApply?.location?.[0]?.city,
                        advFilterApply?.location?.[0]?.state,
                        advFilterApply?.location?.[0]?.country,
                      ]
                        .filter(Boolean)
                        .join(', ')}`}
                      badgeType={WsBadgetype.INACTIVE}
                      endIcon={true}
                      onClick={() => onClearClientLocation?.()}
                      badgeSize={WsBadgeSize.LARGE}
                    />
                  </Grid>
                )}
                {hasFilterValue() && (
                  <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        backgroundColor: WsColorsNew.neutral_400a,
                        height: 30,
                        mx: 0.5,
                      }}
                    />
                    <WsButton onClick={handleClear} variant="text">
                      Clear Filters
                    </WsButton>
                  </Grid>
                )}
              </Grid>
            </form>
          </FormProvider>
        </StyledBox>
      )}
    </>
  );
};

export default ClientFilters;
